import React, { useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { CustomButton } from '../../Infra/CustomButton/CustomButton';
import { useTemplateSelector } from './useTemplateSelector';
import { TemplateSelectorProps } from './types';
import ConfirmationModal from '../../Modal/ConfirmationModal';
import './TemplateSelector.scss';

export const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  onApplySuccess,
  onTemplateApplied,
  initialTemplateId,
  sessionId,
  statusBadge,
  hasEditedRows,
}) => {
  const {
    templates,
    selectedTemplateId,
    isLoading,
    isApplying,
    error,
    setSelectedTemplateId,
    applyRules,
    refreshTemplates,
  } = useTemplateSelector(
    initialTemplateId,
    onApplySuccess,
    sessionId,
    onTemplateApplied
  );

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [overwriteEdits, setOverwriteEdits] = useState(false);

  const handleApplyClick = () => {
    if (hasEditedRows) {
      // Reset checkbox to default (unchecked = preserve edits)
      setOverwriteEdits(false);
      setShowConfirmModal(true);
    } else {
      // No edits, apply directly
      applyRules();
    }
  };

  const handleConfirmApply = () => {
    setShowConfirmModal(false);
    // preserveEditedValues = !overwriteEdits
    // If checkbox is unchecked (default), preserveEditedValues = true (preserve)
    // If checkbox is checked, preserveEditedValues = false (overwrite)
    applyRules(!overwriteEdits);
  };

  if (isLoading) {
    return (
      <Box className="templateSelector templateSelector--loading">
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading templates...
        </Typography>
      </Box>
    );
  }

  if (error && templates.length === 0) {
    return (
      <Box className="templateSelector templateSelector--error">
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  if (templates.length === 0) {
    return (
      <Box className="templateSelector templateSelector--empty">
        <Typography color="text.secondary">
          No configuration templates found. Please create one first.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="templateSelector">
      <Box className="templateSelector__controls">
        <FormControl className="templateSelector__dropdown" size="small">
          <InputLabel id="template-selector-label">Select Template</InputLabel>
          <Select
            labelId="template-selector-label"
            value={selectedTemplateId || ''}
            onChange={e => setSelectedTemplateId(e.target.value)}
            onOpen={refreshTemplates}
            disabled={isApplying}
            label="Select Template"
          >
            {templates.map(template => (
              <MenuItem key={template.id} value={template.id}>
                {template.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <CustomButton
          onClick={handleApplyClick}
          disabled={!selectedTemplateId || isApplying}
          loading={isApplying}
          variant="contained"
          className="templateSelector__button"
        >
          {isApplying ? 'Applying...' : 'Apply Configuration'}
        </CustomButton>

        {statusBadge}
      </Box>

      {/* Confirmation Modal for reapplying with edited rows */}
      <ConfirmationModal
        open={showConfirmModal}
        onCancel={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmApply}
        title="Reapply Configuration?"
        subtitle="You have edited some values in the processed data."
        message="Reapplying configuration will update all fields based on the template mappings. By default, your edited values will be preserved."
        confirmText="Confirm"
        maxWidth="xs"
        dataTestId="reapply-config-modal"
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={overwriteEdits}
              onChange={e => setOverwriteEdits(e.target.checked)}
              data-testid="overwrite-edits-checkbox"
            />
          }
          label="Also overwrite my edited values"
        />
      </ConfirmationModal>
    </Box>
  );
};
